import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, logAnalyticsEvent } from '@/lib/firebase';
import type { User as UserData } from '@shared/types';
import { PLAN_CONFIGS } from '@shared/types';

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        setUserData(null);
        setLoading(false);
      }
    });

    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!user) return;

    const unsubUser = onSnapshot(doc(db, 'users', user.uid), (doc) => {
      if (doc.exists()) {
        setUserData(doc.data() as UserData);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubUser();
  }, [user]);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    logAnalyticsEvent('login', { method: 'email' });
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const starterPlan = PLAN_CONFIGS.starter;

    await setDoc(doc(db, 'users', result.user.uid), {
      fullName,
      companyName: '',
      targetLocation: '',
      selectedNiches: [],
      onboardingCompleted: false,
      subscriptionActive: false,
      planId: 'starter',
      quotas: starterPlan.quotas,
      usage: { leadsUsed: 0, sitesUsed: 0 },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    logAnalyticsEvent('sign_up', { method: 'email' });
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);

    const userDocRef = doc(db, 'users', result.user.uid);
    const existingUser = await getDoc(userDocRef);
    const isNewUser = !existingUser.exists();

    // Only create user document if it doesn't exist
    // This prevents overwriting subscription status for existing users
    if (isNewUser) {
      const starterPlan = PLAN_CONFIGS.starter;

      await setDoc(userDocRef, {
        fullName: result.user.displayName || '',
        companyName: '',
        targetLocation: '',
        selectedNiches: [],
        onboardingCompleted: false,
        subscriptionActive: false,
        planId: 'starter',
        quotas: starterPlan.quotas,
        usage: { leadsUsed: 0, sitesUsed: 0 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    logAnalyticsEvent(isNewUser ? 'sign_up' : 'login', { method: 'google' });
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        loading,
        signIn,
        signUp,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
